if (!customElements.get('search-drawer')) {
  customElements.define(
    'search-drawer',
    class SearchDrawer extends DrawerElement {
      constructor() {
        super();
      }

      get shouldAppendToBody() {
        return false;
      }

      get input() {
        return this.querySelector('input[type="search"]');
      }

      get focusElement() {
        return this.querySelector('input[type="search"]');
      }
    }
  );
}

if (!customElements.get('search-typed')) {
  customElements.define(
    'search-typed',
    class SearchTyped extends HTMLElement {
      constructor() {
        super();

        Motion.inView(this, this.init.bind(this));
      }

      get startDelay() {
        return this.hasAttribute('data-delay') ? parseFloat(this.getAttribute('data-delay')) : 0;
      }

      async init() {
        this.insertCursor();
        await this.start(this.getAttribute('data-first-text'), this.startDelay);

        setTimeout(async () => {
          await this.reset();
          await this.start(this.getAttribute('data-last-text'), 0);
        }, 600);
      }

      async start(text, delay) {
        this.innerHTML = text;
        await Motion.animate(this, { width: [0, `${this.scrollWidth}px`] }, { duration: 1, delay }).finished;
        this.cursor.classList.add('blink');
      }

      async reset() {
        this.cursor.classList.remove('blink');
        await Motion.animate(this, { width: 0 }, { duration: 0.25 }).finished;
      }

      insertCursor() {
        if (this.cursor) return;

        this.cursor = document.createElement('span');
        this.cursor.className = 'typed-cursor';
        this.cursor.setAttribute('aria-hidden', true);
        this.cursor.innerHTML = '|';
        this.parentElement.insertBefore(this.cursor, this.nextSibling);
      }
    }
  );
}

if (!customElements.get('predictive-search')) {
  customElements.define(
    'predictive-search',
    class PredictiveSearch extends HTMLFormElement {
      constructor() {
        super();

        this.cachedMap = new Map();
        this.countCacheMap = new Map();
        this.focusElement = this.input;

        this.resetButton?.addEventListener('click', this.clear.bind(this));
        this.input?.addEventListener('click', this.hideTypewriter.bind(this));
        this.input?.addEventListener('input', theme.utils.debounce(this.onChange.bind(this), 300));
        this.input?.addEventListener('focus', this.onFocus.bind(this));
      }

      get input() {
        return this.querySelector('input[type="search"]');
      }

      get resetButton() {
        return this.querySelector('button[type="reset"]');
      }

      get typewriter() {
        return this.input?.previousElementSibling;
      }

      get useFullSearchProducts() {
        return this.hasAttribute('data-use-full-search-products');
      }

      get suggestResourceTypes() {
        return this.getAttribute('data-suggest-resource-types') || '';
      }

      hideTypewriter() {
        if (this.typewriter && !this.typewriter.hasAttribute('hidden')) {
          this.typewriter.setAttribute('hidden', '');
        }
      }

      clear(event = null) {
        if (event) event.preventDefault();

        if (this.input) {
          this.input.value = '';
          this.input.focus();
        }
        this.removeAttribute('results');
      }

      getQuery() {
        return (this.input?.value || '').trim();
      }

      suggestLimit() {
        const raw = this.getAttribute('data-limit');
        const n = parseInt(raw || '10', 10);
        const parsed = Number.isFinite(n) ? n : 10;
        return Math.min(Math.max(parsed, 1), 10);
      }

      buildSuggestUrl() {
        const url = new URL(`${theme.routes.shop_url}${theme.routes.predictive_search_url}`);
        url.searchParams.set('q', this.getQuery());
        url.searchParams.set('resources[limit]', String(this.suggestLimit()));
        url.searchParams.set('resources[limit_scope]', 'each');
        url.searchParams.set('options[prefix]', 'last');

        const sid = this.getAttribute('data-predictive-api-section-id') || theme.utils.sectionId(this);
        url.searchParams.set('section_id', sid);

        if (this.useFullSearchProducts && this.suggestResourceTypes) {
          url.searchParams.set('resources[type]', this.suggestResourceTypes);
        }

        return url;
      }

      buildFullSearchProductsUrl() {
        const url = new URL(`${theme.routes.shop_url}${theme.routes.search_url}`);
        url.searchParams.set('q', this.getQuery());
        url.searchParams.set('type', 'product');
        url.searchParams.set('options[prefix]', 'last');
        url.searchParams.set('section_id', this.getAttribute('data-full-search-section-id') || 'ck-predictive-search-products');
        return url;
      }

      buildSearchCountUrl() {
        const url = new URL(`${theme.routes.shop_url}/search`);
        url.searchParams.set('q', this.getQuery());
        url.searchParams.set('type', 'product');
        url.searchParams.set('options[prefix]', 'last');
        return url;
      }

      updateViewMoreCount(count) {
        this.querySelectorAll('.ck-search-view-more-count').forEach((element) => {
          const template = element.getAttribute('data-template') || '';
          element.textContent = template.replace('[count]', count);
        });
      }

      fetchAndUpdateProductCount() {
        const query = this.getQuery();
        if (query.length === 0) return;

        const url = this.buildSearchCountUrl().toString();
        if (this.countCacheMap.has(url)) {
          this.updateViewMoreCount(this.countCacheMap.get(url));
          return;
        }

        this.countAbortController?.abort();
        this.countAbortController = new AbortController();

        fetch(url, { signal: this.countAbortController.signal })
          .then((response) => response.text())
          .then((responseText) => {
            const doc = new DOMParser().parseFromString(responseText, 'text/html');
            const countSource = doc.querySelector('#ProductCount, [role="status"]');
            const countText = countSource ? countSource.textContent : '';
            const matches = countText ? countText.match(/\d[\d.,]*/g) : null;
            const parsed = matches
              ? Math.max(...matches.map((n) => parseInt(n.replace(/[^\d]/g, ''), 10)).filter(Boolean))
              : 0;

            if (parsed > 0) {
              this.countCacheMap.set(url, parsed);
              this.updateViewMoreCount(parsed);
            }
          })
          .catch((error) => {
            if (error.name !== 'AbortError') console.error(error);
          });
      }

      onFocus(event) {
        if (this.closest('.collection')) {
          document.body.classList.add('predictive-search-open');

          const searchTerm = this.getQuery();
          if (searchTerm.length === 0) return;

          if (this.useFullSearchProducts) {
            this.renderMerged();
          } else {
            const url = this.buildSuggestUrl().toString();
            this.renderSection(url, event);
          }
        }
      }

      onChange() {
        if (this.getQuery().length === 0) {
          this.clear();
          return;
        }

        this.hideTypewriter();

        if (this.useFullSearchProducts) {
          this.renderMerged();
        } else {
          const url = this.buildSuggestUrl().toString();
          this.renderSection(url);
        }
      }

      renderSection(url) {
        this.cachedMap.has(url) ? this.renderSectionFromCache(url) : this.renderSectionFromFetch(url);
      }

      renderSectionFromCache(url) {
        const responseText = this.cachedMap.get(url);
        this.renderSearchResults(responseText);
        this.setAttribute('results', '');
      }

      renderSectionFromFetch(url) {
        this.abortController?.abort();
        this.abortController = new AbortController();

        this.setAttribute('loading', '');

        fetch(url, { signal: this.abortController.signal })
          .then((response) => response.text())
          .then((responseText) => {
            this.renderSearchResults(responseText);
            this.cachedMap.set(url, responseText);
            this.fetchAndUpdateProductCount();

            this.removeAttribute('loading');
            this.setAttribute('results', '');
          })
          .catch((error) => {
            if (error.name === 'AbortError') return;
            console.error(error);
          });
      }

      findInParsedDoc(doc, exactId, idPrefix) {
        let el = doc.getElementById(exactId);
        if (!el) {
          try {
            el = doc.querySelector(`#${CSS.escape(exactId)}`);
          } catch {
            el = null;
          }
        }
        if (!el && idPrefix) {
          el = doc.querySelector(`[id^="${idPrefix}"]`);
        }
        return el;
      }

      renderSearchResults(responseText) {
        const sid = theme.utils.sectionId(this);
        if (!sid) return;

        const id = 'PredictiveSearchResults-' + sid;
        const live = document.getElementById(id);
        if (!live) return;

        const doc = new DOMParser().parseFromString(responseText, 'text/html');
        const src = this.findInParsedDoc(doc, id, 'PredictiveSearchResults-');
        if (!src) return;

        live.innerHTML = src.innerHTML;
      }

      mergedCacheKey() {
        return `${this.buildSuggestUrl().toString()}\n${this.buildFullSearchProductsUrl().toString()}`;
      }

      renderMerged() {
        const cacheKey = this.mergedCacheKey();
        if (this.cachedMap.has(cacheKey)) {
          const { suggestText, fullText } = this.cachedMap.get(cacheKey);
          this.applyMergedResults(suggestText, fullText);
          this.setAttribute('results', '');
          return;
        }

        this.abortController?.abort();
        this.abortController = new AbortController();

        this.setAttribute('loading', '');

        const suggestUrl = this.buildSuggestUrl().toString();
        const fullUrl = this.buildFullSearchProductsUrl().toString();

        Promise.allSettled([
          fetch(suggestUrl, { signal: this.abortController.signal }).then((r) => r.text()),
          fetch(fullUrl, { signal: this.abortController.signal }).then((r) => r.text()),
        ])
          .then(([suggestResult, fullResult]) => {
            const suggestText = suggestResult.status === 'fulfilled' ? suggestResult.value : '';
            const fullText = fullResult.status === 'fulfilled' ? fullResult.value : '';
            this.cachedMap.set(cacheKey, { suggestText, fullText });

            this.applyMergedResults(suggestText, fullText);
            this.fetchAndUpdateProductCount();

            this.removeAttribute('loading');
            this.setAttribute('results', '');
          })
          .catch((error) => {
            if (error?.name === 'AbortError') return;
            console.error(error);
            this.removeAttribute('loading');
          });
      }

      applyMergedResults(suggestText, fullText) {
        const sid = theme.utils.sectionId(this);
        if (!sid) return;

        const parser = new DOMParser();
        const suggestDoc = parser.parseFromString(suggestText, 'text/html');

        const nonProductsId = 'PredictiveSearchNonProducts-' + sid;
        const liveNon = document.getElementById(nonProductsId);
        const srcNon = this.findInParsedDoc(suggestDoc, nonProductsId, 'PredictiveSearchNonProducts-');
        if (liveNon && srcNon) liveNon.innerHTML = srcNon.innerHTML;

        const collectionsId = 'PredictiveSearchCollections-' + sid;
        const liveCol = document.getElementById(collectionsId);
        const srcCol = this.findInParsedDoc(suggestDoc, collectionsId, 'PredictiveSearchCollections-');
        if (liveCol && srcCol) liveCol.innerHTML = srcCol.innerHTML;

        const productsTarget = document.querySelector(`#PredictiveSearchProducts-${sid} [data-ck-predictive-products-target]`);
        if (productsTarget) {
          const fullDoc = parser.parseFromString(fullText, 'text/html');
          const inner = fullDoc.querySelector('[data-ck-predictive-products-inner]');
          const fullCards = inner ? Array.from(inner.children) : [];

          // The full-text /search drops sold-out products (Shopify ranks them to the bottom,
          // past the loaded window). Merge those back in from the Predictive Search API, and
          // honour the Bestseller tag: tagged items go to the front, the rest to the end.
          const source = suggestDoc.querySelector('[data-ck-predictive-products-source]');
          const predictiveCards = source ? Array.from(source.content.children) : [];
          const priorityHandles = new Set(
            (source?.getAttribute('data-ck-priority-handles') || '').split(',').filter(Boolean)
          );

          const merged = this.mergeProductCards(fullCards, predictiveCards, priorityHandles);
          productsTarget.innerHTML = merged.map((card) => card.outerHTML).join('');
        }

        this.updatePredictiveMeta(sid);
      }

      productHandleFromCard(card) {
        if (!card) return '';
        const link = card.matches?.('a[href*="/products/"]')
          ? card
          : card.querySelector?.('a[href*="/products/"]');
        const href = link?.getAttribute('href') || '';
        const match = href.match(/\/products\/([^/?#]+)/);
        return match ? match[1] : '';
      }

      mergeProductCards(fullCards, predictiveCards, priorityHandles = new Set()) {
        const limit = this.suggestLimit();

        // Track handles already shown by the full-text results so we never duplicate a product.
        const seen = new Set(fullCards.map((card) => this.productHandleFromCard(card)).filter(Boolean));

        // Products the full-text search dropped, split by Bestseller tag.
        const front = [];
        const end = [];
        predictiveCards.forEach((card) => {
          const handle = this.productHandleFromCard(card);
          if (!handle || seen.has(handle)) return;
          seen.add(handle);
          (priorityHandles.has(handle) ? front : end).push(card);
        });

        if (front.length === 0 && end.length === 0) return fullCards.slice(0, limit);

        // Bestseller-tagged items first, then the relevance-ranked full results, then the rest.
        return [...front, ...fullCards, ...end].slice(0, limit);
      }

      updatePredictiveMeta(sid) {
        const results = document.getElementById('PredictiveSearchResults-' + sid);
        if (!results) return;

        const emptyEl = results.querySelector('[data-ck-empty-state]');
        const moreEl = results.querySelector('[data-ck-view-more]');
        if (!emptyEl || !moreEl) return;

        const non = document.getElementById('PredictiveSearchNonProducts-' + sid);
        const prodTarget = document.querySelector(`#PredictiveSearchProducts-${sid} [data-ck-predictive-products-target]`);
        const col = document.getElementById('PredictiveSearchCollections-' + sid);

        const hasNon = non && non.textContent.trim().length > 0;
        const hasProd = prodTarget && prodTarget.children.length > 0;
        const hasCol = col && col.textContent.trim().length > 0;
        const onlyProducts = hasProd && !hasNon && !hasCol;

        results.classList.toggle('ck-only-products', onlyProducts);

        emptyEl.classList.add('hidden');
        moreEl.classList.add('hidden');

        if (!hasNon && !hasProd && !hasCol) {
          emptyEl.classList.remove('hidden');
          const termsHost = emptyEl.querySelector('[data-ck-empty-terms]');
          if (termsHost) termsHost.textContent = this.getQuery();
        } else {
          moreEl.classList.remove('hidden');
        }
      }
    },
    { extends: 'form' }
  );
}

if (!customElements.get('predictive-search-overlay')) {
  customElements.define(
    'predictive-search-overlay',
    class PredictiveSearchOverlay extends OverlayElement {
      constructor() {
        super();

        this.addEventListener('click', this.onClick);
      }

      onClick() {
        setTimeout(() => {
          document.body.classList.remove('predictive-search-open');
        });
      }
    }
  );
}

